import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, User, CreditCard, FileText, Users, Phone, 
  Save, Settings, Unlock, CheckCircle, Upload, Download,
  AlertTriangle, Shield, Euro, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../components/ui/sonner';
import * as api from '../../services/api';

const ClientManagementPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('actions');

  const fetchClient = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getClient(clientId);
      // Initialize banking data if not present
      if (!data.accounts || data.accounts.length === 0) {
        data.accounts = [
          { type: 'apport', balance: 0, status: 'pending', currency: 'EUR' },
          { type: 'credit', balance: data.loanAmount || 0, status: 'blocked', currency: 'EUR' }
        ];
      }
      if (!data.rib) {
        data.rib = {
          iban: 'FR76 3000 4028 3700 0100 0000 123',
          bic: 'FTNOFRP1XXX',
          accountHolder: `${data.firstName} ${data.lastName}`,
          bankName: 'Fortuneo Banque'
        };
      }
      if (!data.contact) {
        data.contact = {
          advisorName: 'Service Client Fortuneo',
          advisorPhone: '01 46 09 49 49',
          advisorEmail: 'contact@fortuneo.fr',
          agencyAddress: '110 Esplanade du Général de Gaulle, 92931 Paris La Défense'
        };
      }
      if (!data.beneficiaries) {
        data.beneficiaries = [];
      }
      setClient(data);
    } catch (error) {
      toast.error('Erreur de chargement du client');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate]);

  useEffect(() => {
    fetchClient();
  }, [fetchClient]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Quick Actions
  const handleUnlockCreditAccount = async () => {
    try {
      setSaving(true);
      const newAccounts = client.accounts.map(acc => 
        acc.type === 'credit' ? { ...acc, status: 'active' } : acc
      );
      await api.updateAccounts(client.id, newAccounts);
      toast.success('Compte crédit débloqué avec succès !');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors du déblocage');
    } finally {
      setSaving(false);
    }
  };

  const handleValidateApport = async () => {
    try {
      setSaving(true);
      const newAccounts = client.accounts.map(acc => 
        acc.type === 'apport' ? { ...acc, status: 'active', balance: client.contribution } : acc
      );
      await api.updateAccounts(client.id, newAccounts);
      toast.success('Réception de l\'apport validée !');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };

  const handleBlockCreditAccount = async () => {
    try {
      setSaving(true);
      const newAccounts = client.accounts.map(acc => 
        acc.type === 'credit' ? { ...acc, status: 'blocked' } : acc
      );
      await api.updateAccounts(client.id, newAccounts);
      toast.success('Compte crédit bloqué');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors du blocage');
    } finally {
      setSaving(false);
    }
  };

  const handleResetApport = async () => {
    try {
      setSaving(true);
      const newAccounts = client.accounts.map(acc => 
        acc.type === 'apport' ? { ...acc, status: 'pending', balance: 0 } : acc
      );
      await api.updateAccounts(client.id, newAccounts);
      toast.success('Apport réinitialisé');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  // Account updates
  const handleSaveAccounts = async () => {
    try {
      setSaving(true);
      await api.updateAccounts(client.id, client.accounts);
      toast.success('Comptes mis à jour');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRIB = async () => {
    try {
      setSaving(true);
      await api.updateRIB(client.id, client.rib);
      toast.success('RIB mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      setSaving(true);
      await api.updateContact(client.id, client.contact);
      toast.success('Contact mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  // Beneficiaries
  const handleAddBeneficiary = async () => {
    const name = prompt('Nom du bénéficiaire:');
    if (!name) return;
    const iban = prompt('IBAN du bénéficiaire:');
    if (!iban) return;
    const bic = prompt('BIC (optionnel):') || '';
    
    try {
      await api.addBeneficiary(client.id, { name, iban, bic });
      toast.success('Bénéficiaire ajouté');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteBeneficiary = async (beneficiaryId) => {
    if (!window.confirm('Supprimer ce bénéficiaire ?')) return;
    try {
      await api.deleteBeneficiary(client.id, beneficiaryId);
      toast.success('Bénéficiaire supprimé');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Offer PDF
  const handleUploadOffer = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileData = e.target.result;
        await api.uploadOfferPdf(client.id, {
          fileData,
          fileName: file.name,
          fileType: file.type
        });
        toast.success('Offre PDF uploadée');
        await fetchClient();
        setSaving(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      setSaving(false);
    }
  };

  // Delete Offer PDF
  const handleDeleteOffer = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette offre PDF ?')) return;
    
    try {
      setSaving(true);
      await api.deleteOfferPdf(client.id);
      toast.success('Offre PDF supprimée');
      await fetchClient();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const apportAccount = client.accounts?.find(a => a.type === 'apport') || { balance: 0, status: 'pending' };
  const creditAccount = client.accounts?.find(a => a.type === 'credit') || { balance: 0, status: 'blocked' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')}
                className="flex items-center"
                data-testid="back-button"
              >
                <ArrowLeft size={18} className="mr-2" />
                Retour
              </Button>
              <div className="border-l pl-4">
                <h1 className="text-lg font-semibold text-gray-900">
                  {client.firstName} {client.lastName}
                </h1>
                <p className="text-sm text-gray-500">ID: {client.id}</p>
              </div>
            </div>
            <Badge className="bg-green-100 text-green-800">Administration</Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Client Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Compte Apport</p>
                  <p className="text-2xl font-bold">{formatCurrency(apportAccount.balance)}</p>
                </div>
                <Badge className={apportAccount.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                  {apportAccount.status === 'active' ? 'Actif' : 'En attente'}
                </Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Compte Crédit</p>
                  <p className="text-2xl font-bold">{formatCurrency(creditAccount.balance)}</p>
                </div>
                <Badge className={creditAccount.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {creditAccount.status === 'active' ? 'Débloqué' : 'Bloqué'}
                </Badge>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Montant prêt</p>
                  <p className="text-2xl font-bold">{formatCurrency(client.loanAmount)}</p>
                </div>
                <Badge className="bg-green-100 text-green-800">{client.status}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="actions" className="flex items-center gap-2" data-testid="tab-actions">
              <Settings size={16} />
              <span className="hidden sm:inline">Actions</span>
            </TabsTrigger>
            <TabsTrigger value="accounts" className="flex items-center gap-2" data-testid="tab-accounts">
              <CreditCard size={16} />
              <span className="hidden sm:inline">Comptes</span>
            </TabsTrigger>
            <TabsTrigger value="rib" className="flex items-center gap-2" data-testid="tab-rib">
              <FileText size={16} />
              <span className="hidden sm:inline">RIB</span>
            </TabsTrigger>
            <TabsTrigger value="beneficiaries" className="flex items-center gap-2" data-testid="tab-beneficiaries">
              <Users size={16} />
              <span className="hidden sm:inline">Bénéficiaires</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2" data-testid="tab-contact">
              <Phone size={16} />
              <span className="hidden sm:inline">Contact</span>
            </TabsTrigger>
            <TabsTrigger value="info" className="flex items-center gap-2" data-testid="tab-info">
              <User size={16} />
              <span className="hidden sm:inline">Infos</span>
            </TabsTrigger>
          </TabsList>

          {/* ACTIONS TAB - Most Important */}
          <TabsContent value="actions" data-testid="actions-content">
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="text-green-600" />
                    Actions rapides
                  </CardTitle>
                  <CardDescription>
                    Gérez les comptes et validations pour ce client
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Unlock Credit Account */}
                    <Card className={`border-2 ${creditAccount.status === 'blocked' ? 'border-orange-200 bg-orange-50' : 'border-green-200 bg-green-50'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${creditAccount.status === 'blocked' ? 'bg-orange-100' : 'bg-green-100'}`}>
                            <Unlock className={creditAccount.status === 'blocked' ? 'text-orange-600' : 'text-green-600'} size={24} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">Compte Crédit</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              {creditAccount.status === 'blocked' 
                                ? 'Le compte crédit est actuellement bloqué' 
                                : 'Le compte crédit est débloqué et actif'}
                            </p>
                            {creditAccount.status === 'blocked' ? (
                              <Button 
                                onClick={handleUnlockCreditAccount} 
                                disabled={saving}
                                className="w-full bg-orange-600 hover:bg-orange-700"
                                data-testid="unlock-credit-btn"
                              >
                                <Unlock size={16} className="mr-2" />
                                Débloquer le compte crédit
                              </Button>
                            ) : (
                              <Button 
                                onClick={handleBlockCreditAccount} 
                                disabled={saving}
                                variant="outline"
                                className="w-full border-red-300 text-red-600 hover:bg-red-50"
                                data-testid="block-credit-btn"
                              >
                                <AlertTriangle size={16} className="mr-2" />
                                Bloquer le compte
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Validate Apport */}
                    <Card className={`border-2 ${apportAccount.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : 'border-green-200 bg-green-50'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${apportAccount.status === 'pending' ? 'bg-yellow-100' : 'bg-green-100'}`}>
                            <CheckCircle className={apportAccount.status === 'pending' ? 'text-yellow-600' : 'text-green-600'} size={24} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold">Apport Personnel</h3>
                            <p className="text-sm text-gray-600 mb-1">
                              Montant attendu: {formatCurrency(client.contribution)}
                            </p>
                            <p className="text-sm text-gray-600 mb-3">
                              {apportAccount.status === 'pending' 
                                ? 'En attente de réception' 
                                : `Apport reçu: ${formatCurrency(apportAccount.balance)}`}
                            </p>
                            {apportAccount.status === 'pending' ? (
                              <Button 
                                onClick={handleValidateApport} 
                                disabled={saving}
                                className="w-full bg-green-600 hover:bg-green-700"
                                data-testid="validate-apport-btn"
                              >
                                <CheckCircle size={16} className="mr-2" />
                                Valider la réception de l&apos;apport
                              </Button>
                            ) : (
                              <Button 
                                onClick={handleResetApport} 
                                disabled={saving}
                                variant="outline"
                                className="w-full"
                                data-testid="reset-apport-btn"
                              >
                                Réinitialiser
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Offer PDF Upload */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="text-green-600" />
                    Offre Fortuneo
                  </CardTitle>
                  <CardDescription>
                    Uploadez l&apos;offre de prêt personnalisée pour ce client
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <Label htmlFor="offer-upload" className="cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-green-400 transition-colors">
                          <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                          <p className="text-sm text-gray-600">Cliquez pour uploader l&apos;offre PDF</p>
                          <p className="text-xs text-gray-400 mt-1">PDF uniquement</p>
                        </div>
                      </Label>
                      <input
                        id="offer-upload"
                        type="file"
                        accept=".pdf"
                        onChange={handleUploadOffer}
                        className="hidden"
                        data-testid="offer-upload-input"
                      />
                    </div>
                    {client.offerPdfUrl && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="text-green-600" size={20} />
                          <span className="text-sm text-green-800 font-medium">Offre uploadée</span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                const data = await api.downloadOfferPdf(client.id);
                                if (data.fileData) {
                                  const link = document.createElement('a');
                                  link.href = data.fileData;
                                  link.download = data.fileName || 'offre_fortuneo.pdf';
                                  link.click();
                                }
                              } catch (error) {
                                toast.error('Erreur de téléchargement');
                              }
                            }}
                            data-testid="download-offer-btn"
                          >
                            <Download size={14} className="mr-1" />
                            Voir
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleDeleteOffer}
                            disabled={saving}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            data-testid="delete-offer-btn"
                          >
                            <Trash2 size={14} className="mr-1" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Status Update */}
              <Card>
                <CardHeader>
                  <CardTitle>Statut du dossier</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {['En attente', 'En cours', 'Approuvé', 'Refusé'].map((status) => (
                      <Button
                        key={status}
                        variant={client.status === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.updateClient(client.id, { status });
                            toast.success(`Statut mis à jour: ${status}`);
                            await fetchClient();
                          } catch (error) {
                            toast.error('Erreur');
                          }
                        }}
                        data-testid={`status-${status.toLowerCase().replace(' ', '-')}`}
                      >
                        {status}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ACCOUNTS TAB */}
          <TabsContent value="accounts" data-testid="accounts-content">
            <Card>
              <CardHeader>
                <CardTitle>Configuration des comptes bancaires</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Loan Details */}
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h3 className="font-semibold text-green-800 mb-4">Détails du prêt</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Montant du prêt (€)</Label>
                      <Input
                        type="number"
                        value={client.loanAmount || 0}
                        onChange={(e) => setClient({ ...client, loanAmount: parseFloat(e.target.value) || 0 })}
                        className="mt-1 bg-white"
                        data-testid="loan-amount"
                      />
                    </div>
                    <div>
                      <Label>Durée (années)</Label>
                      <Input
                        type="number"
                        value={client.loanDuration || 0}
                        onChange={(e) => setClient({ ...client, loanDuration: parseInt(e.target.value) || 0 })}
                        className="mt-1 bg-white"
                        data-testid="loan-duration"
                      />
                    </div>
                    <div>
                      <Label>Taux d&apos;intérêt (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={client.interestRate || 3.5}
                        onChange={(e) => setClient({ ...client, interestRate: parseFloat(e.target.value) || 0 })}
                        className="mt-1 bg-white"
                        data-testid="interest-rate"
                      />
                    </div>
                    <div>
                      <Label>Mensualité (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={client.monthlyPayment || 0}
                        onChange={(e) => setClient({ ...client, monthlyPayment: parseFloat(e.target.value) || 0 })}
                        className="mt-1 bg-white"
                        placeholder="Ex: 662.78"
                        data-testid="monthly-payment"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Apport personnel (€)</Label>
                    <Input
                      type="number"
                      value={client.contribution || 0}
                      onChange={(e) => setClient({ ...client, contribution: parseFloat(e.target.value) || 0 })}
                      className="mt-1 bg-white"
                      data-testid="contribution"
                    />
                  </div>
                  <div className="mt-4">
                    <Label>Versement reçu (€)</Label>
                    <Input
                      type="number"
                      value={client.receivedAmount || 0}
                      onChange={(e) => setClient({ ...client, receivedAmount: parseFloat(e.target.value) || 0 })}
                      className="mt-1 bg-white"
                      placeholder="Montant versé par le client"
                      data-testid="received-amount"
                    />
                    <p className="text-xs text-gray-500 mt-1">Montant déjà versé par le client sur son apport personnel</p>
                  </div>
                  <Button 
                    onClick={async () => {
                      try {
                        setSaving(true);
                        await api.updateClient(client.id, {
                          loanAmount: client.loanAmount,
                          loanDuration: client.loanDuration,
                          interestRate: client.interestRate,
                          contribution: client.contribution,
                          monthlyPayment: client.monthlyPayment,
                          receivedAmount: client.receivedAmount
                        });
                        toast.success('Détails du prêt mis à jour');
                        await fetchClient();
                      } catch (error) {
                        toast.error('Erreur lors de la mise à jour');
                      } finally {
                        setSaving(false);
                      }
                    }} 
                    disabled={saving} 
                    className="w-full mt-4"
                    data-testid="save-loan-details-btn"
                  >
                    <Save size={16} className="mr-2" />
                    Enregistrer les détails du prêt
                  </Button>
                </div>

                {/* Accounts */}
                {client.accounts.map((account, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center gap-2 mb-4">
                      <Euro className="text-green-600" />
                      <h3 className="font-semibold">
                        {account.type === 'apport' ? 'Compte Apport Personnel' : 'Compte Crédit Immobilier'}
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Solde (€)</Label>
                        <Input
                          type="number"
                          value={account.balance}
                          onChange={(e) => {
                            const newAccounts = [...client.accounts];
                            newAccounts[index].balance = parseFloat(e.target.value) || 0;
                            setClient({ ...client, accounts: newAccounts });
                          }}
                          className="mt-1"
                          data-testid={`account-balance-${account.type}`}
                        />
                      </div>
                      <div>
                        <Label>Statut</Label>
                        <select
                          value={account.status}
                          onChange={(e) => {
                            const newAccounts = [...client.accounts];
                            newAccounts[index].status = e.target.value;
                            setClient({ ...client, accounts: newAccounts });
                          }}
                          className="w-full border rounded-md p-2 mt-1 bg-white"
                          data-testid={`account-status-${account.type}`}
                        >
                          <option value="pending">En attente</option>
                          <option value="active">Actif</option>
                          <option value="blocked">Bloqué</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
                <Button onClick={handleSaveAccounts} disabled={saving} className="w-full" data-testid="save-accounts-btn">
                  <Save size={16} className="mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer les comptes'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RIB TAB */}
          <TabsContent value="rib" data-testid="rib-content">
            <Card>
              <CardHeader>
                <CardTitle>Configuration du RIB</CardTitle>
                <CardDescription>
                  Ces informations seront visibles par le client dans son espace
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label>IBAN</Label>
                    <Input
                      value={client.rib?.iban || ''}
                      onChange={(e) => setClient({ ...client, rib: { ...client.rib, iban: e.target.value } })}
                      className="mt-1 font-mono"
                      data-testid="rib-iban"
                    />
                  </div>
                  <div>
                    <Label>BIC</Label>
                    <Input
                      value={client.rib?.bic || ''}
                      onChange={(e) => setClient({ ...client, rib: { ...client.rib, bic: e.target.value } })}
                      className="mt-1 font-mono"
                      data-testid="rib-bic"
                    />
                  </div>
                  <div>
                    <Label>Nom de la banque</Label>
                    <Input
                      value={client.rib?.bankName || ''}
                      onChange={(e) => setClient({ ...client, rib: { ...client.rib, bankName: e.target.value } })}
                      className="mt-1"
                      data-testid="rib-bank"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Titulaire du compte</Label>
                    <Input
                      value={client.rib?.accountHolder || ''}
                      onChange={(e) => setClient({ ...client, rib: { ...client.rib, accountHolder: e.target.value } })}
                      className="mt-1"
                      data-testid="rib-holder"
                    />
                  </div>
                </div>
                <Button onClick={handleSaveRIB} disabled={saving} className="w-full" data-testid="save-rib-btn">
                  <Save size={16} className="mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer le RIB'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* BENEFICIARIES TAB */}
          <TabsContent value="beneficiaries" data-testid="beneficiaries-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Bénéficiaires</CardTitle>
                    <CardDescription>Gérez les bénéficiaires du client</CardDescription>
                  </div>
                  <Button onClick={handleAddBeneficiary} data-testid="add-beneficiary-btn">
                    Ajouter
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {client.beneficiaries?.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="mx-auto mb-2" size={40} />
                    <p>Aucun bénéficiaire enregistré</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {client.beneficiaries?.map((ben) => (
                      <div key={ben.id} className="border rounded-lg p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                          <p className="font-medium">{ben.name}</p>
                          <p className="text-sm text-gray-500 font-mono">{ben.iban}</p>
                          {ben.bic && <p className="text-xs text-gray-400">BIC: {ben.bic}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBeneficiary(ben.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-beneficiary-${ben.id}`}
                        >
                          Supprimer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONTACT TAB */}
          <TabsContent value="contact" data-testid="contact-content">
            <Card>
              <CardHeader>
                <CardTitle>Informations de contact</CardTitle>
                <CardDescription>
                  Coordonnées du conseiller visibles par le client
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du conseiller</Label>
                    <Input
                      value={client.contact?.advisorName || ''}
                      onChange={(e) => setClient({ ...client, contact: { ...client.contact, advisorName: e.target.value } })}
                      className="mt-1"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      value={client.contact?.advisorPhone || ''}
                      onChange={(e) => setClient({ ...client, contact: { ...client.contact, advisorPhone: e.target.value } })}
                      className="mt-1"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Email</Label>
                    <Input
                      value={client.contact?.advisorEmail || ''}
                      onChange={(e) => setClient({ ...client, contact: { ...client.contact, advisorEmail: e.target.value } })}
                      className="mt-1"
                      data-testid="contact-email"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Adresse de l&apos;agence</Label>
                    <Input
                      value={client.contact?.agencyAddress || ''}
                      onChange={(e) => setClient({ ...client, contact: { ...client.contact, agencyAddress: e.target.value } })}
                      className="mt-1"
                      data-testid="contact-address"
                    />
                  </div>
                </div>
                <Button onClick={handleSaveContact} disabled={saving} className="w-full" data-testid="save-contact-btn">
                  <Save size={16} className="mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer le contact'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* INFO TAB */}
          <TabsContent value="info" data-testid="info-content">
            <Card>
              <CardHeader>
                <CardTitle>Informations personnelles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Prénom</p>
                    <p className="font-medium">{client.firstName}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium">{client.lastName}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm break-all">{client.email}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Téléphone</p>
                    <p className="font-medium">{client.phone}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Date de naissance</p>
                    <p className="font-medium">{client.dateOfBirth}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Profession</p>
                    <p className="font-medium">{client.profession}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg sm:col-span-2 lg:col-span-3">
                    <p className="text-sm text-gray-500">Adresse</p>
                    <p className="font-medium">{client.address}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Revenus mensuels</p>
                    <p className="font-medium">{formatCurrency(client.monthlyIncome)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Montant du prêt</p>
                    <p className="font-medium">{formatCurrency(client.loanAmount)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Apport personnel</p>
                    <p className="font-medium">{formatCurrency(client.contribution)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Durée du prêt</p>
                    <p className="font-medium">{client.loanDuration} ans</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Date de création</p>
                    <p className="font-medium">{client.createdAt}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200 sm:col-span-2 lg:col-span-3">
                    <p className="text-sm text-green-600 font-medium mb-2">🔐 Mot de passe du client</p>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="text"
                        value={client.plainPassword || ''}
                        onChange={(e) => setClient({ ...client, plainPassword: e.target.value })}
                        placeholder="Définir le mot de passe"
                        className="flex-1"
                        data-testid="admin-password-input"
                      />
                      <Button
                        onClick={async () => {
                          try {
                            await api.updateClient(client.id, { plainPassword: client.plainPassword });
                            toast.success('Mot de passe mis à jour !');
                            await fetchClient();
                          } catch (error) {
                            toast.error('Erreur lors de la mise à jour');
                          }
                        }}
                        style={{ backgroundColor: '#82C43C' }}
                        size="sm"
                        data-testid="admin-save-password-btn"
                      >
                        <Save size={16} className="mr-1" />
                        Enregistrer
                      </Button>
                    </div>
                    <p className="text-xs text-green-600 mt-2">Le client pourra se connecter avec ce mot de passe.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ClientManagementPage;
